import { type ReactNode, useEffect, useState } from "react";

import type { CryptoSelectOption } from "../CryptoSelect";
import type { CryptoComboboxPresProps } from "./CryptoCombobox.pres";

export interface UseCryptoComboboxInput {
  value: string;
  onChange: (value: string) => void;
  options: CryptoSelectOption[];
  emptyMessage?: ReactNode;
}

export type UseCryptoComboboxOutput = Pick<
  CryptoComboboxPresProps,
  | "selectedOption"
  | "filteredOptions"
  | "showPanel"
  | "showSelectedIcon"
  | "displayValue"
  | "onSelect"
  | "onQueryChange"
  | "onBlur"
>;

export const useCryptoCombobox = ({
  value,
  onChange,
  options,
  emptyMessage,
}: UseCryptoComboboxInput): UseCryptoComboboxOutput => {
  const [query, setQuery] = useState("");
  const selectedOption = options.find((option) => option.value === value);

  // Reset local query when the consumer-controlled value clears (e.g. chain change)
  useEffect(() => {
    if (!value) {
      setQuery("");
    }
  }, [value]);

  const trimmed = query.trim().toLowerCase();
  const filteredOptions = trimmed
    ? options.filter((option) => option.label.toLowerCase().includes(trimmed))
    : options;

  const hasNoMatches =
    trimmed !== "" && filteredOptions.length === 0 && !!emptyMessage;
  const showPanel = filteredOptions.length > 0 || hasNoMatches;
  const showSelectedIcon = !!selectedOption?.icon && !query;

  const onSelect = (v: string | null) => {
    onChange(v ?? "");
    setQuery("");
  };

  const displayValue = (v: string) =>
    options.find((o) => o.value === v)?.label ?? "";

  return {
    selectedOption,
    filteredOptions,
    showPanel,
    showSelectedIcon,
    displayValue,
    onSelect,
    onQueryChange: setQuery,
    onBlur: () => setQuery(""),
  };
};
